/**
 * User profile page with dark mode support
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Save } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAppStore } from '../stores/appStore';
import { useUpdateProfile, useChangePassword } from '../hooks/useAuth';
import { getInitials, cn } from '../lib/utils';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

const profileSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Senha atual obrigatoria'),
  newPassword: z.string().min(6, 'Nova senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Senhas nao conferem',
  path: ['confirmPassword'],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  // SEO meta tags for profile page
  useSEO(SEO_CONFIGS.profile);

  const user = useAppStore((state) => state.user);
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = (data: ProfileForm) => {
    updateProfile.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordForm) => {
    changePassword.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    }, {
      onSuccess: () => {
        resetPasswordForm();
        setShowPasswordForm(false);
      },
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Meu Perfil</h1>

      {/* Profile Info */}
      <div className="card p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold',
            'bg-primary-100 dark:bg-primary-900/30',
            'text-primary-600 dark:text-primary-400'
          )}>
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(user?.name || '')
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">{user?.name}</h2>
            <p className="text-neutral-500 dark:text-neutral-400">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
          <Input
            {...registerProfile('name')}
            label="Nome"
            error={profileErrors.name?.message}
          />

          <Button
            type="submit"
            loading={updateProfile.isPending}
            icon={<Save className="w-4 h-4" />}
          >
            Salvar Alterações
          </Button>
        </form>
      </div>

      {/* Password Change */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Alterar Senha</h2>
        </div>

        {showPasswordForm ? (
          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <Input
              {...registerPassword('currentPassword')}
              type="password"
              label="Senha Atual"
              error={passwordErrors.currentPassword?.message}
              showPasswordToggle
            />

            <Input
              {...registerPassword('newPassword')}
              type="password"
              label="Nova Senha"
              error={passwordErrors.newPassword?.message}
              showPasswordToggle
            />

            <Input
              {...registerPassword('confirmPassword')}
              type="password"
              label="Confirmar Nova Senha"
              error={passwordErrors.confirmPassword?.message}
              showPasswordToggle
            />

            <div className="flex gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  resetPasswordForm();
                  setShowPasswordForm(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={changePassword.isPending}
              >
                Alterar Senha
              </Button>
            </div>
          </form>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowPasswordForm(true)}
          >
            Alterar minha senha
          </Button>
        )}
      </div>
    </div>
  );
}
